import { useId } from 'react';
import { FaRegStar, FaStar } from 'react-icons/fa';

interface StarRatingProps {
  rating: number;
  onRating: (rating: number) => void;
  'aria-label'?: string;
}

const STARS = [1, 2, 3, 4, 5];

/* Marcada e vazia diferem pela FORMA (cheia × contorno), não só pela cor: num tema de contraste
   do Windows o navegador pinta tudo com a mesma cor, e com a mesma forma as cinco pareciam marcadas.
   Os rádios são nativos: um único marcado por grupo e setas do teclado sem código próprio. */
export function StarRating({ rating, onRating, 'aria-label': ariaLabel }: StarRatingProps) {
  const groupName = useId();

  return (
    <div className="flex flex-col items-center gap-1 px-4 pb-4">
      <div className="flex gap-1" role="radiogroup" aria-label={ariaLabel || 'Avaliação por estrelas'}>
        {STARS.map(star => {
          const filled = rating >= star;
          const Icon = filled ? FaStar : FaRegStar;

          return (
            <label key={star} className="cursor-pointer">
              <input
                type="radio"
                name={groupName}
                value={star}
                checked={rating === star}
                onChange={() => onRating(star)}
                aria-label={`${star} estrela${star > 1 ? 's' : ''}`}
                className="peer sr-only"
              />
              <span
                className={`block rounded transition-transform duration-200 hover:scale-110 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-600 ${
                  filled ? 'text-amber-600' : 'text-slate-500'
                }`}
              >
                <Icon size={28} aria-hidden="true" />
              </span>
            </label>
          );
        })}
      </div>
      {/* A nota em texto: quem não distingue as estrelas lê o número. O leitor de tela já ouve o rádio marcado. */}
      <span className="text-sm font-semibold text-slate-800" aria-hidden="true">
        {rating ? `${rating} de 5` : 'Sem nota'}
      </span>
    </div>
  );
}
